/**
 * Cloud Agent — block metadata (GENUI-3). Pure, React-free.
 *
 * Single source of truth for "which block types exist" and "what does each render
 * with". `BLOCK_RENDER_TARGET` is typed `Record<BlockType, ...>`, so adding a block
 * type to the contract without listing it here is a compile error — this is the
 * structural half of the "renderer handles every block.type" guarantee (the other
 * half is the exhaustive `switch` in GenerativePage).
 */
import { BlockType, ComputationBlockType, HydratedBlock } from '../../../../../services/admin/cloudAgentTypes';

/** Every block type, prose + interactive blocks included. */
export const ALL_BLOCK_TYPES: readonly BlockType[] = [
  'kpiStat',
  'lineChart',
  'barChart',
  'donut',
  'rankBars',
  'scatter',
  'table',
  'callout',
  'sectionHeading',
  'filter',
] as const;

/** The data-backed (numeric) block types — these carry `data` + `source`. */
export const COMPUTATION_BLOCK_TYPES: readonly ComputationBlockType[] = [
  'kpiStat',
  'lineChart',
  'barChart',
  'donut',
  'rankBars',
  'scatter',
  'table',
] as const;

/** Which existing/dumb component each block type renders with (docs + test anchor). */
export const BLOCK_RENDER_TARGET: Record<BlockType, string> = {
  kpiStat: 'StatCard',
  lineChart: 'GraphCard',
  barChart: 'BarChartCard',
  donut: 'DonutChart',
  rankBars: 'SideBarChart',
  scatter: 'ScatterChart',
  table: 'TableComponent',
  callout: 'Callout',
  sectionHeading: 'heading',
  filter: 'MultiSelectFilter',
};

/**
 * Computations whose `rankBars` labels ARE supplier/platform names, so resolving a label to a
 * supplier logo is correct. Everything else labels its bars with RESTAURANTS (or products),
 * where the logo lookup matches by EXACT name and would render e.g. Illy's logo for a restaurant
 * called "Illy" — the wrong identity for that row, not just the wrong icon. Venues sharing a
 * name with a wholesaler are not hypothetical.
 *
 * An allowlist rather than a denylist on purpose: a new rankBars computation is far more likely
 * to be labelled with something other than suppliers, so the safe default is plain text and a
 * computation has to be vouched for to get logos. See `RankBarsBlock` for which server-side
 * computations are exposed today.
 */
export const SUPPLIER_LABELLED_COMPUTATIONS: readonly string[] = ['spendBySupplier'] as const;

export function isSupplierLabelled(computation?: string): boolean {
  return !!computation && SUPPLIER_LABELLED_COMPUTATIONS.indexOf(computation) !== -1;
}

/** A numeric/computation-backed block shows the subtle `source` affordance. */
export function isComputationBlock(
  block: HydratedBlock,
): block is Exclude<
  HydratedBlock,
  { type: 'callout' } | { type: 'sectionHeading' } | { type: 'filter' }
> {
  return block.type !== 'callout' && block.type !== 'sectionHeading' && block.type !== 'filter';
}
